import { ClusterSummary } from "./api";

/**
 * The column set of V2's mission-control table, in V2's order. V2 labels its columns with the raw
 * keys (name, organizationName, ...), and the column picker and CSV export both work off this
 * list, so keeping one ordered definition here keeps table, picker and export in step.
 */
export const COLUMN_KEYS = [
  "name",
  "accountId",
  "clusterUniqueId",
  "organizationName",
  "organizationId",
  "domainUrl",
  "region",
  "state",
  "type",
  "cloudProvider",
  "environmentType",
  "computeUnits",
  "conductorVersion",
  "agentVersion",
  "workersVersion",
  "eksVersion",
  "vpcCidr",
  "active",
  "healthy",
  "orkesHosted",
  "multitenant",
  "sku",
  "createdAt",
] as const;

export type ColumnKey = (typeof COLUMN_KEYS)[number];

/** V2's default visible columns. */
export const DEFAULT_COLUMNS: ColumnKey[] = [
  "name",
  "organizationName",
  "state",
  "type",
  "cloudProvider",
  "region",
];

/**
 * Columns the backend can sort by. The listing SQL sorts on whitelisted clusters/organizations
 * columns only, so the derived and joined ones (versions, sku, healthy, hosting) are display-only
 * and must not offer a sort control that the server would ignore.
 */
export const SORTABLE_COLUMNS: ColumnKey[] = [
  "name",
  "accountId",
  "organizationName",
  "domainUrl",
  "region",
  "state",
  "type",
  "cloudProvider",
  "environmentType",
  "computeUnits",
  "createdAt",
];

/** Value of one cell, as text. Also what CSV export writes, so the two can never disagree. */
export function cellValue(cluster: ClusterSummary, key: ColumnKey): string {
  switch (key) {
    case "name":
      return cluster.name ?? "";
    case "accountId":
      return cluster.accountId ?? "";
    case "clusterUniqueId":
      return cluster.id ?? "";
    case "organizationName":
      return cluster.organizationName ?? "";
    case "organizationId":
      return cluster.organizationId ?? "";
    case "domainUrl":
      return cluster.domainUrl ?? "";
    case "region":
      return cluster.region ?? "";
    case "state":
      return cluster.state ?? "";
    case "type":
      return cluster.type ?? "";
    case "cloudProvider":
      return cluster.cloudProvider ?? "";
    case "environmentType":
      return cluster.environmentType ?? "";
    case "computeUnits":
      return cluster.computeUnits == null ? "" : String(cluster.computeUnits);
    case "conductorVersion":
      return cluster.appVersions?.conductor ?? "";
    case "agentVersion":
      return cluster.appVersions?.agent ?? "";
    case "workersVersion":
      return cluster.appVersions?.workers ?? "";
    case "eksVersion":
      return cluster.eksVersion ?? "";
    case "vpcCidr":
      return cluster.vpcCidr ?? "";
    case "active":
      return String(cluster.active);
    case "healthy":
      // V2 shows this column but the backing stat is not computed yet; it renders as blank
      // rather than claiming a health status the server never determined.
      return "";
    case "orkesHosted":
      return cluster.isOrkesHosted ? "Orkes hosted" : "Customer hosted";
    case "multitenant":
      return String(cluster.isMultitenant);
    case "sku":
      return cluster.skuId?.name ?? "";
    case "createdAt":
      return formatCreatedAt(cluster.createdAt);
    default:
      return "";
  }
}

/** The server sends ISO-8601 UTC; an unparseable value renders as itself rather than "Invalid Date". */
export function formatCreatedAt(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

/** RFC 4180 quoting: a field containing a comma, quote or newline must be quoted. */
export function toCsv(rows: string[][]): string {
  return rows
    .map((row) =>
      row
        .map((field) =>
          /[",\n\r]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field
        )
        .join(",")
    )
    .join("\n");
}

/**
 * What clicking a cluster name should do. Mission Control lists every organization's clusters
 * while the details page fetches within the active one, so opening a cluster that belongs to
 * another organization needs that organization made active first. V2 does the same: it switches,
 * then routes once the switch lands.
 *
 * Returned separately from the component so the rule is testable without rendering, and so the
 * "no switch needed" case stays an ordinary link.
 */
export function clusterNavigationPlan(
  clusterOrganizationId: string | null | undefined,
  activeOrganizationId: string | null | undefined
): { needsOrganizationSwitch: boolean; organizationId?: string } {
  if (
    !clusterOrganizationId ||
    !activeOrganizationId ||
    clusterOrganizationId === activeOrganizationId
  ) {
    return { needsOrganizationSwitch: false };
  }
  return {
    needsOrganizationSwitch: true,
    organizationId: clusterOrganizationId,
  };
}
